import type { UrlTargeting } from 'types';

/**
 * The dashboard's half of "Open in editor": asks the Pertento Editor extension to open a page
 * with a variant picked in its side panel. Website, experiment and variant, so it serves an
 * experiment's variant as well as a feature flag's On side.
 *
 * The names match the extension's `src/apps/editor/extension/src/shared/open-editor.ts`, which
 * the dashboard does not import; change both together. The protocol is in
 * docs/frontend/editor-extension-bridge.md, "Opening the editor from the dashboard".
 */
export const OPEN_EDITOR_PAGE_MESSAGE = 'PERTENTO_OPEN_EDITOR';
/** The content script's marker, which useGlobalAlerts also looks for. */
export const EXTENSION_MARKER_ID = 'pertento-extension-installed';
/** On the marker when the extension understands OPEN_EDITOR_PAGE_MESSAGE; its value is the version. */
export const OPEN_EDITOR_ATTRIBUTE = 'data-open-editor';

export type EditorTarget = { websiteId: number; experimentId: number; variantId: number; url: string };

/**
 * Whether the installed extension takes the message. An older one sets the marker without the
 * attribute, and without an extension there is no marker: either way the caller keeps its
 * fallback (open the site, say what to pick).
 */
export function isOpenEditorSupported(doc: Document) {
  const version = Number(doc.getElementById(EXTENSION_MARKER_ID)?.getAttribute(OPEN_EDITOR_ATTRIBUTE));
  return Number.isInteger(version) && version >= 1;
}

type PostOpenEditorParams = { target: EditorTarget; win: Window };

/**
 * Posted to the dashboard's own window and origin, never `'*'`: the content script takes it
 * only when its source is this window and its origin this page's. It has to run inside the
 * click's handler, so the extension can still open its side panel (a user gesture).
 */
export function postOpenEditor({ target, win }: PostOpenEditorParams) {
  const { websiteId, experimentId, variantId, url } = target;
  win.postMessage(
    { type: OPEN_EDITOR_PAGE_MESSAGE, request: { websiteId, experimentId, variantId, url } },
    win.location.origin,
  );
}

const httpUrl = (value: string, base?: string) => {
  try {
    const url = new URL(value, base);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
};

/** A website's URL as stored, with `https://` added when it was saved without a scheme. */
export function websitePageUrl(websiteUrl: string | null | undefined) {
  const trimmed = websiteUrl?.trim();
  if (!trimmed) return null;
  return /^[a-z][a-z\d+.-]*:/i.test(trimmed) ? httpUrl(trimmed) : httpUrl(`https://${trimmed}`);
}

type Rule = Pick<UrlTargeting, 'id' | 'url' | 'condition'>;

/** Conditions whose value names a page that can be opened as it is. */
const namesPage = (rule: Rule) => rule.condition === 'equals' || rule.condition === 'starts with';

type PageUrlParams = { urlTargeting?: readonly Rule[]; websiteUrl?: string | null };

/**
 * The page to open the editor on: the first URL targeting rule (by id) that names a page,
 * `equals` or `starts with` an absolute URL or a path on the website, else the website itself.
 * A `contains`, `ends with`, regex or negative rule names no page, so it is skipped.
 */
const sameHost = (a: string, b: string) => {
  try {
    return new URL(a).host === new URL(b).host;
  } catch {
    return false;
  }
};

export function pageUrlFor({ urlTargeting = [], websiteUrl }: PageUrlParams) {
  const site = websitePageUrl(websiteUrl);
  const rules = [...urlTargeting].sort((a, b) => a.id - b.id);
  for (const rule of rules) {
    if (!namesPage(rule) || !rule.url?.trim()) continue;
    const value = rule.url.trim();
    const page = value.startsWith('/') ? (site ? httpUrl(value, site) : null) : httpUrl(value);
    // Only a page on the website itself: a rule naming another host (or a protocol-relative
    // `//other.site/x`) must not send a colleague's Open in editor somewhere else.
    if (page && site && sameHost(page, site)) return page;
  }
  return site;
}
